import clientParams, { bookingsPageValidations } from "./common_variable";
import * as Yup from "yup";
import { Modal } from "antd";
// import { staffList } from "@/apis/staff/staffApi";
// import { staffList } from "./staff_list";
import { useSelector, useDispatch } from "react-redux";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import API_ENDPOINTS from "./api";
import api from "@/services/axios_custom";
import dayjs from "dayjs";
import { Entity_types, Security_Types } from "./permission_bits";
import { usePermissionMask } from "./usePermissionMask";
import { SYSTEM_SECURITY_DEFAULTS } from "./systemSecurityDefaults";
import {
  setUserSecurityUpdated,
  setUserDetail,
  setUserDetailLoading,
} from "@/slices/userDetailDetail";
import {
  parseCriteriaString,
  evaluateCriteriaNode,
} from "./globalCriteriaParser";
import _ from "lodash";

// Security tiers, ported from PCI's CPCISecurityInterface.cls
// (WhatAreMyRightsInternal / prv_GetAllowSettingsForALevel / CombineRulesWithOperator).
// System and Global are reserved StaffGroupsID values, not real assignable
// groups (SecurityHelper.bas: SECURITYSPECIAL_SYSTEMGROUPID = 1,
// SECURITYSPECIAL_GLOBALGROUPID = 2) — they apply to every user regardless
// of group membership.
const RULE_TIER = {
  SYSTEM: "system",
  GLOBAL: "global",
  GROUP: "group",
  PERSONAL: "personal",
};
export const SPECIAL_GROUP_ID = { SYSTEM: 1, GLOBAL: 2 };

const mapRuleItem = (src, tier, parsePermissionMask) => {
  const allowSetting =
    isNonEmptyVariable(src?.allowSetting) && !isNaN(src?.allowSetting)
      ? parseInt(src?.allowSetting)
      : 0;
  const denySetting =
    isNonEmptyVariable(src?.denySetting) && !isNaN(src?.denySetting)
      ? parseInt(src?.denySetting)
      : 0;
  return {
    ...src,
    allowSetting: parsePermissionMask(allowSetting),
    denySetting: parsePermissionMask(denySetting),
    _tier: tier,
  };
};

// OR together every matching active rule's Allow/Deny bits in the given set
// (CombineRulesWithOperator, always called with "OR").
const combineRaw = (rules, permission) => {
  let allow = false;
  let deny = false;
  for (const rule of rules) {
    if (!rule.ruleActive) continue;
    if (rule.allowSetting?.[permission]) allow = true;
    if (rule.denySetting?.[permission]) deny = true;
  }
  return { allow, deny };
};

// System -> Global -> Group -> Personal cascade, empirically validated
// against real PCI test cases (a user in two groups with conflicting rules
// for the same module/permission). Confirmed behavior: the Group tier does
// NOT flat-combine every group's rules into one bag before masking — each
// group the staff belongs to is resolved as its OWN independent path against
// the System+Global baseline, and the Group tier's result is the union (OR)
// of every group's own outcome. A stricter group does not take away what a
// more permissive group specifically grants; only a Personal-level rule (the
// most specific tier) can override what the Group union already grants.
//
// Per client requirement, System only seeds the initial baseline here (the
// first tier in the priority order) - it is deliberately NOT re-applied as a
// final override after Global/Group/Personal have run, so a lower tier's
// explicit allow can now stand even where System denied by default.
const resolveEffectiveAllow = (matchingRules, permission) => {
  const byTier = (tier) => matchingRules.filter((r) => r._tier === tier);

  const system = combineRaw(byTier(RULE_TIER.SYSTEM), permission);
  const global = combineRaw(byTier(RULE_TIER.GLOBAL), permission);
  const personal = combineRaw(byTier(RULE_TIER.PERSONAL), permission);

  let allow = !system.deny;
  allow = (allow || global.allow) && !global.deny;

  const groupRules = byTier(RULE_TIER.GROUP);
  const groupIds = [...new Set(groupRules.map((r) => r.staffGroupsId))];
  if (groupIds.length) {
    const beforeGroup = allow;
    allow = groupIds.some((id) => {
      const thisGroup = combineRaw(
        groupRules.filter((r) => r.staffGroupsId === id),
        permission,
      );
      return (beforeGroup || thisGroup.allow) && !thisGroup.deny;
    });
  }

  allow = (allow || personal.allow) && !personal.deny;

  return allow;
};

var refLists = [];
var staffList = [];
var judgeLists = [];
var user = null;
const SetReflist = () => {
  const refList = useSelector((state) => state?.refListReducer?.refLists);
  const stafflist = useSelector((state) => state?.staff?.activeList);
  const judgelist = useSelector((state) => state?.staff?.judgeList);
  useEffect(() => {
    const loginUser = JSON.parse(localStorage.getItem("userDetails"));
    if (isNonEmptyVariable(loginUser)) {
      user = loginUser;
    }
  }, []);

  useEffect(() => {
    refLists = refList;
  }, [refList]);
  useEffect(() => {
    staffList = stafflist;
  }, [stafflist]);
  useEffect(() => {
    judgeLists = judgelist;
  }, [judgelist]);
};

var clientParamsLocal = [];
const SetClientParam = () => {
  const globalClientParam = useSelector((state) => state?.clientParams?.list);

  useEffect(() => {
    if (isNonEmptyVariable(globalClientParam)) {
      clientParamsLocal = [...clientParams, ...globalClientParam];
    }
  }, [globalClientParam]);
};

const groupIdOf = (group) => group?.staffGroupsID ?? group?.staffGroupsId;

// Classify a commonStaffGroups[] entry by its reserved StaffGroupsID.
const tierForCommonGroup = (group) => {
  const id = groupIdOf(group);
  if (id === SPECIAL_GROUP_ID.SYSTEM) return RULE_TIER.SYSTEM;
  if (id === SPECIAL_GROUP_ID.GLOBAL) return RULE_TIER.GLOBAL;
  return RULE_TIER.GROUP;
};

// GlobalCriteria definitions (id -> { tableName, parsed tree }), and the
// current user's own agency (for resolving "@MYAGENCY"-style variable
// tokens inside a criteria's ValueSet). Both are set once at login.
var globalCriteriaById = new Map();
var currentUserAgency = null;

var securityRules = null;
const SetSecurityRules = () => {
  const { parsePermissionMask } = usePermissionMask();
  const { userDetail } = useSelector((state) => state?.userDetail);
  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;

    const build = async () => {
      if (!isNonEmptyVariable(userDetail)) return;

      // /profile returns staff (Personal), staffGroup (the real groups this
      // staff is a member of), and commonStaffGroups (the reserved
      // System/StaffGroupsID-1 and Global-Agency-Wide/StaffGroupsID-2
      // pseudo-groups that apply to everyone regardless of membership) in one
      // atomic response. Fall back to the login payload if that call fails,
      // which at least still covers Personal and Group.
      let profile = null;
      try {
        const { data } = await api.get(API_ENDPOINTS.profile);
        profile = data;
      } catch {
        profile = null;
      }
      if (cancelled) return;

      // GlobalCriteria definitions (the actual criteriaString per rule) are
      // fetched once and cached, parsed up front so hasCriteriaAccess never
      // has to re-parse on every call.
      try {
        const { data } = await api.get(
          API_ENDPOINTS.security.globalCriteria.getAll,
        );
        globalCriteriaById = new Map(
          (data || []).map((row) => [
            row.globalCriteriaID,
            {
              name: row.criteriaName,
              tableName: row.tableName,
              parsed: parseCriteriaString(row.criteriaString),
            },
          ]),
        );
      } catch {
        globalCriteriaById = new Map();
      }
      if (cancelled) return;

      const securityRulesList = _.cloneDeep(profile ?? userDetail);
      if (!isNonEmptyVariable(securityRulesList)) return;

      const personalSource = securityRulesList?.staff ?? {};
      const groupSources = Array.isArray(securityRulesList?.staffGroup)
        ? securityRulesList.staffGroup
        : [];
      const commonGroupSources = Array.isArray(
        securityRulesList?.commonStaffGroups,
      )
        ? securityRulesList.commonStaffGroups
        : [];

      currentUserAgency = personalSource?.organization?.[0]?.agency ?? null;

      const buildTier = (ruleKey) => [
        ...(personalSource[ruleKey] || []).map((src) =>
          mapRuleItem(src, RULE_TIER.PERSONAL, parsePermissionMask),
        ),
        ...groupSources.flatMap((group) =>
          (group?.[ruleKey] || []).map((src) =>
            mapRuleItem(src, RULE_TIER.GROUP, parsePermissionMask),
          ),
        ),
        ...commonGroupSources.flatMap((group) =>
          (group?.[ruleKey] || []).map((src) =>
            mapRuleItem(src, tierForCommonGroup(group), parsePermissionMask),
          ),
        ),
        ...(ruleKey === "securityRulesFunctionality"
          ? SYSTEM_SECURITY_DEFAULTS.map((src) =>
              mapRuleItem(src, RULE_TIER.SYSTEM, parsePermissionMask),
            )
          : []),
      ];

      securityRules = {
        securityRulesCriteria: buildTier("securityRulesCriteria"),
        securityRulesFunctionality: buildTier("securityRulesFunctionality"),
        securityRulesRecords: buildTier("securityRulesRecords"),
      };

      // Signal "security rules are actually ready to be checked" — anything
      // that gates on this (e.g. the Go menu's module-visibility filter)
      // must wait for this, not just for userDetail to exist, since this
      // effect's own /profile + GlobalCriteria fetches resolve later than
      // userDetail does.
      if (!cancelled) dispatch(setUserSecurityUpdated(true));
    };

    build();

    return () => {
      cancelled = true;
    };
  }, [userDetail, parsePermissionMask]);
};

// Does the given staffId belong to the currently logged-in session?
const isLoggedInStaff = (staffId) => {
  const loginUser = JSON.parse(localStorage.getItem("userDetails"));
  return (
    isNonEmptyVariable(staffId) &&
    isNonEmptyVariable(loginUser?.staffId) &&
    String(staffId) === String(loginUser.staffId)
  );
};

// Is the currently logged-in staff a member of a security group, given that
// group's staffGroupMembers rows (from fetchStaffGroupById's response)?
const isLoggedInStaffInGroup = (staffGroupMembers) => {
  const loginUser = JSON.parse(localStorage.getItem("userDetails"));
  if (!isNonEmptyVariable(loginUser?.staffId)) return false;
  return (staffGroupMembers || []).some((member) =>
    isLoggedInStaff(member?.memberStaffId),
  );
};

// After a Staff/Security-module CRUD save that affects the logged-in staff's
// own record or group membership, re-pull /profile (the same endpoint
// SetSecurityRules itself builds the permission cascade from), reassign the
// "userDetails" localStorage snapshot to the fresh data, and push it into
// redux — which re-triggers SetSecurityRules' effect above so the Personal/
// Group/System/Global rule tiers get rebuilt off the latest security data.
const refreshLoggedInUserSecurity = async (dispatch) => {
  const loginUser = JSON.parse(localStorage.getItem("userDetails"));
  if (!isNonEmptyVariable(loginUser?.staffId)) return;

  try {
    dispatch(setUserDetailLoading(true));
    const { data: profile } = await api.get(API_ENDPOINTS.profile);
    if (isNonEmptyVariable(profile)) {
      const updatedUserDetails = { ...loginUser, ...profile };
      localStorage.setItem("userDetails", JSON.stringify(updatedUserDetails));
      localStorage.setItem("userProfile", JSON.stringify(profile));
      localStorage.removeItem("allowedMenu");
      dispatch(setUserSecurityUpdated(false));
      dispatch(setUserDetail(updatedUserDetails));

      // Modal.info({
      //   title: "Security Updated",
      //   content:
      //     "Your staff and security details have been updated. Click OK to refresh the page.",
      //   okText: "OK",
      //   centered: true,
      //   maskClosable: false,
      //   closable: false,
      //   onOk: () => window.location.reload(),
      // });
    }
  } catch (error) {
  } finally {
    dispatch(setUserDetailLoading(false));
  }
};

// Row-level security check (PCI's GlobalCriteria/GetCriteriaByContextAndUser).
// Unlike hasCheckSecurity, this needs the actual record: a Criteria rule
// stores a "deny-target" expression (e.g. "Sealed = TRUE"), and ASP ORs every
// applicable rule's expression together, then wraps the whole thing in NOT —
// i.e. a record is hidden if ANY applicable rule's expression matches it.
// `permission` gates which rules are "applicable" the same way Functionality
// rules do (allowSetting[permission] && !denySetting[permission]), across
// the same Personal/Group/Global/System tiers already merged into
// securityRules.securityRulesCriteria.
export const hasCriteriaAccess = (tableName, permission, record) => {
  const rules = securityRules?.securityRulesCriteria || [];
  const context = { MYAGENCY: currentUserAgency };

  const applicable = rules.filter((rule) => {
    if (!rule.ruleActive) return false;
    if (!rule.allowSetting?.[permission] || rule.denySetting?.[permission]) {
      return false;
    }
    const def = globalCriteriaById.get(rule.globalCriteriaID);
    return def && def.tableName?.toLowerCase() === tableName?.toLowerCase();
  });

  const isDenied = applicable.some((rule) => {
    const def = globalCriteriaById.get(rule.globalCriteriaID);
    return evaluateCriteriaNode(def.parsed, record, context);
  });

  return !isDenied;
};

// Recursively collects every leaf's literal ValueSet entries under a parsed
// criteria node (skips "@"-prefixed variable tokens like "@MYAGENCY", since
// those aren't references to a specific RefListItem).
const collectLiteralLeafValues = (node, acc) => {
  if (!node) return;
  if (node.type === "clause") {
    (node.children || []).forEach((child) =>
      collectLiteralLeafValues(child, acc),
    );
    return;
  }
  (node.valueSet || []).forEach((v) => {
    if (typeof v === "string" && v.startsWith("@")) return;
    acc.push(String(v));
  });
};

// Finds every cached GlobalCriteria rule whose criteriaString references a
// given RefListItem (matched by RefListItemGlobalID, the stable cross-table
// key RefList-backed criteria leaves store in their ValueSet — see
// RefListItems.refListItemGlobalID). Used to block deleting a RefList item
// that's still relied on by a security/criteria rule, mirroring PCI's
// AllowDeleteRecord / DeleteSubObjectAllowed delete-guard convention (there is
// no literal ASP precedent for this specific RefList<->GlobalCriteria check —
// it never existed in the legacy source — so this follows the closest real
// convention instead of porting one).
export const findGlobalCriteriaUsingRefListItem = (refListItemGlobalID) => {
  if (!isNonEmptyVariable(refListItemGlobalID)) return [];
  const target = String(refListItemGlobalID);
  const matches = [];
  globalCriteriaById.forEach((def, globalCriteriaID) => {
    const values = [];
    collectLiteralLeafValues(def?.parsed, values);
    if (values.includes(target)) {
      matches.push({
        globalCriteriaID,
        name: def?.name || `#${globalCriteriaID}`,
      });
    }
  });
  return matches;
};

var modulesList = null;
var submoduleList = null;
var reportList = null;
var tableViewsList = null;
const SetModulesList = () => {
  const { configModules, configSubModules, reports, tablesViewsNew } =
    useSelector((state) => state?.config);
  useEffect(() => {
    if (isNonEmptyVariable(configModules)) {
      modulesList = configModules;
    }

    if (isNonEmptyVariable(configSubModules)) {
      submoduleList = configSubModules;
    }

    if (isNonEmptyVariable(reports)) {
      reportList = reports;
    }

    if (isNonEmptyVariable(reports)) {
      tableViewsList = tablesViewsNew;
    }
  }, [configModules, configSubModules, reports, tablesViewsNew]);
};

/**
 * Checks if the object is empty.
 * @param {Object} obj - The object to check.
 * @returns {boolean} - Returns true if the object is empty, false otherwise.
 */
const isEmptyObject = (obj) => {
  if (obj === null || typeof obj === "undefined") return true;
  if (Object.keys(obj)?.length === 0) {
    return true;
  }
  const everyScan = Object.keys(obj).every((o) => {
    return !isNonEmptyVariable(obj[o]);
  });

  return everyScan;
};

/**
 * Converts an object to an array of key-value pairs with custom labels.
 * @param {Object} obj - The object to convert.
 * @param {String} keyLabel - The label for the key in the resulting objects.
 * @param {String} valueLabel - The label for the value in the resulting objects.
 * @returns {Array} - An array of objects with custom key-value labels.
 */
const objectToArray = (obj, keyLabel = "key", valueLabel = "value") => {
  if (Object.keys(obj).length === 0) {
    return [];
  }

  return Object.keys(obj)
    .filter((key) => isNonEmptyVariable(obj[key])) // Filter out keys with empty values
    .map((key) => ({
      [keyLabel]: key,
      [valueLabel]: obj[key],
    }));
};

/**
 * Converts an object to a query string, supporting nested objects.
 *
 * @param {Object} obj - The object to convert to query parameters.
 * @param {String} [prefix] - Optional prefix for nested keys.
 * @returns {String} - The resulting query string.
 */
const toQueryString = (obj, prefix) => {
  const queryString = Object.keys(obj).map((key) => {
    const value = obj[key];
    const prefixedKey = prefix ? `${prefix}[${key}]` : key;

    if (typeof value === "object" && value !== null) {
      return toQueryString(value, prefixedKey);
    } else {
      return `${encodeURIComponent(prefixedKey)}=${encodeURIComponent(value)}`;
    }
  });

  return queryString.join("&");
};

/**
 * Checks if a variable is non-empty.
 *
 * A variable is considered non-empty if it is not undefined, null, or an empty string.
 *
 * @param {*} value - The variable to check.
 * @returns {boolean} - Returns true if the variable is non-empty; otherwise, false.
 */
const isNonEmptyVariable = (value) => {
  return (
    typeof value !== "undefined" &&
    value !== null &&
    value !== "" &&
    value !== "undefined"
  );
};

/**
 * Creates a debounced version of a function that delays its execution
 * until after a specified wait time has passed since it was last called.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The delay in milliseconds to wait before invoking the function.
 * @returns {Function} - A debounced function that delays calling `func` until after `wait` milliseconds.
 */
const debounce = (func, wait) => {
  let timeout;

  return function (...args) {
    const context = this;

    clearTimeout(timeout);

    timeout = setTimeout(() => {
      func.apply(context, args);
    }, wait);
  };
};

/**
 * Generates an array of numbers from 1 to the given number.
 *
 * @param {number|string} number - The number up to which the array should be generated.
 *                                 Can be a number or a string representation of a number.
 * @returns {number[]} - An array containing numbers from 1 to the given number.
 */
const generateArray = (number) => {
  // Ensure the input is a number by parsing it into an integer.
  number = parseInt(number);

  // Create an array of the specified length (number),
  // then use Array.from to fill it with numbers from 1 to `number`.
  return Array.from({ length: number }, (_, i) => i + 1);
};

/**
 * To diplay component if it has permission
 *
 * @param {string} paramContext - This arguments is used to check with condition if it is there or not
 * @returns {Boolean} - It will returns boolean value
 */
const checkField = (paramContext) => {
  return clientParamsLocal?.find(
    (cp) => cp?.paramContext === paramContext && cp?.paramName === "Display",
  )?.paramValue;
};

/**
 * Generates a validation schema by using Yup validator
 *
 * @param {string} paramContext - This arguments is used to build validator
 * @returns {Object} - An object will return with entire schema that contains all matched conditions
 */
const checkFieldValidation = (
  paramContext,
  paramType = "text",
  label = "",
  clientList = null,
) => {
  return new Promise((resolve, reject) => {
    let schema = Yup;

    switch (paramType) {
      case "text":
        schema = schema.string();
        break;
      case "number":
        schema = schema.number();
        break;
      default:
        schema = schema.string();
        break;
    }

    const clientParamValidation = Object.values(clientList)
      .map((item) => item.paramContext)
      .filter(
        (context) => context.toLowerCase() === paramContext?.toLowerCase(),
      );

    // const clientParamValidation = clientList?.filter(
    //     (cp) => cp?.paramContext?.toLowerCase() === paramContext?.toLowerCase()
    // );

    if (!clientParamValidation || clientParamValidation.length === 0) {
      // If no validations are found, resolve schema as optional
      return resolve(schema.optional());
    }

    let isValid = true;
    clientList?.forEach((cpv, index) => {
      if (cpv?.paramName?.toLowerCase() === "required" && cpv?.paramValue) {
        schema = schema.required(`${label || "This field"} is required`); // Add required validation
        isValid = false;
      }

      if (cpv?.paramName?.toLowerCase() === "regularexpression") {
        if (cpv?.paramValue?.includes("||")) {
          const [pattern, message] = cpv?.paramValue?.split("||") || ["", ""];
          schema = schema.matches(
            new RegExp(pattern),
            message || "Invalid format",
          ); // Add regex validation
          isValid = false;
        }
      }

      if (index === clientList?.length - 1) {
        if (isValid) {
          schema = schema.optional();
        }

        resolve(schema);
      }
    });
  });
};

/**
 * Retrieves a list of options from the reference list based on the provided key.
 * - Includes a default "Select" option at the beginning of the list.
 * - Sorts the retrieved items in descending order by `Sequence_Number`.
 * - Handles cases where the reference list or its items are undefined gracefully.
 *
 * @param {string} key - The name of the reference list to search for.
 * @returns {Array<Object>} - An array of objects representing the options,
 *                            with a default "Select" option included at the start.
 *
 */
const getOptionsFromRefList = (
  key,
  isStaff = false,
  longStringRequired = false,
  isID = false,
  refListGlobalID = "",
  refListID = "",
  allowedGlobalIDs = [],
) => {
  if (key === "sex") {
    key = "gender";
  }
  if (isStaff) {
    return staffList
      ?.map((sl) => ({
        value: sl?.staffId,
        label: longStringRequired ? sl?.staffDescriptionCached : sl?.userName,
      }))
      .filter((item) => item.value != null && item.label != null)
      .sort((a, b) => {
        const isAlphaA = /^[A-Za-z]/.test(a.label);
        const isAlphaB = /^[A-Za-z]/.test(b.label);

        // If one starts with a letter and the other doesn't, prioritize the letter one
        if (isAlphaA && !isAlphaB) return -1;
        if (!isAlphaA && isAlphaB) return 1;

        // If both are same type (both alpha or both non-alpha), sort alphabetically
        return a.label.localeCompare(b.label, undefined, {
          sensitivity: "base",
        });
      });
  }
  if (key === "Judge List") {
    return judgeLists
      ?.map((sl) => ({
        value: sl?.addressBookId,
        label: [sl?.lastName, sl?.firstName].filter(Boolean).join(", "),
      }))
      .filter((item) => item.value != null && item.label != null);
  }

  let refList = [];
  if (refListGlobalID !== "") {
    refList = refLists?.find((rl) => rl?.refListGlobalID == refListGlobalID);
  } else if (refListID !== "") {
    refList = refLists?.find((rl) => rl?.refListId === Number(refListID));
  } else {
    refList = refLists?.find(
      (rl) => rl?.refListName?.toLowerCase() === key?.toLowerCase(),
    );
  }

  const isLongString =
    refList?.displayFieldName === "Long_String" || !!longStringRequired;

  const getItemLabel = (rli) =>
    isLongString && isNonEmptyVariable(rli?.longString)
      ? rli?.longString
      : rli?.defaultString;

  let sortedItems =
    refList?.refListItems
      ?.filter((rli) => {
        if (!rli?.enabled) return false;
        if (allowedGlobalIDs.length > 0) {
          return allowedGlobalIDs.includes(rli?.refListItemGlobalId);
        }
        return true;
      })
      .sort((a, b) =>
        (getItemLabel(a) || "").localeCompare(
          getItemLabel(b) || "",
          undefined,
          {
            sensitivity: "base",
          },
        ),
      ) || [];

  if (sortedItems?.length) {
    sortedItems.unshift({
      refListItemGlobalId: "",
      defaultString: "",
      longString: "",
    });
  }

  return sortedItems?.map((aa) => {
    return {
      value: isID ? aa?.refListItemGlobalId : aa?.defaultString,
      enabled: aa?.enabled,
      // Always available alongside value so callers that need the numeric ID
      // for a lookup (e.g. destination-list-by-loan-type) don't have to flip
      // `isID` and change what `value` means for every other consumer.
      refListItemGlobalId: aa?.refListItemGlobalId,
      label:
        isLongString && isNonEmptyVariable(aa?.longString)
          ? aa?.longString
          : aa?.defaultString,
    };
  });
};

const getRefListItemGlobalIdByDefaultString = (key, defaultString) => {
  const refList = refLists?.find(
    (rl) => rl?.refListName?.toLowerCase() === key?.toLowerCase(),
  );
  const item = refList?.refListItems?.find(
    (rli) => rli?.defaultString === defaultString && rli?.enabled,
  );
  return item?.refListItemGlobalId ?? null;
};

// A BookingQueueStep is the legacy "FinalStep" (removes the booking from
// the active queue on completion) only if its constantName is the
// Remove-from-Queue step — driven by the stable constant, not a hardcoded
// refListItemGlobalId, so it keeps working across environments/reflist edits.
const isRemoveFromQueueStep = (queStepID) => {
  const refList = refLists?.find(
    (rl) => rl?.refListName?.toLowerCase() === "bookingqueuestep",
  );
  const item = refList?.refListItems?.find(
    (rli) => rli?.refListItemGlobalId === queStepID,
  );
  return item?.constantName === "BOOKINGQUEUESTEP_REMOVEFROMQUEUE";
};

// Maps a BookingQueueStep's constantName suffix to the short key used
// throughout the Bookings/Booking Queue UI (table columns, prerequisites).
const BOOKING_QUEUE_STEP_SHORT_NAMES = {
  INTAKE: "int",
  MEDICAL: "med",
  CLASSIFICATION: "cls",
  CHARGES: "chg",
  ASSOCIATIONS: "asn",
  FLAGS: "jf",
  VICTIMS: "v",
  BOOKINGCOMPLETE: "bc",
  MUGSHOT: "ms",
  HOUSING: "hsg",
  REMOVEFROMQUEUE: "cp",
};

// Mirrors the legacy BookingQueueStep reflist's per-step "Prerequisites"
// attribute: only Booking Complete and Remove-from-Queue have prerequisite
// steps configured (everything else can be done in any order). Note Remove
// from Queue does NOT require Flags or Victim Notification, matching legacy.
const BOOKING_QUEUE_STEP_PREREQUISITES = {
  bc: ["int", "chg", "asn"],
  cp: ["int", "med", "cls", "chg", "asn", "bc", "ms", "hsg"],
};

const getBookingQueueStepConstantName = (queStepID) => {
  const refList = refLists?.find(
    (rl) => rl?.refListName?.toLowerCase() === "bookingqueuestep",
  );
  return (
    refList?.refListItems?.find((rli) => rli?.refListItemGlobalId === queStepID)
      ?.constantName ?? null
  );
};

const getBookingQueueStepShortName = (queStepID) => {
  const refList = refLists?.find(
    (rl) => rl?.refListName?.toLowerCase() === "bookingqueuestep",
  );
  const item = refList?.refListItems?.find(
    (rli) => rli?.refListItemGlobalId === queStepID,
  );
  const constantSuffix = item?.constantName?.includes("BOOKINGQUEUESTEP_")
    ? item.constantName.split("_")[1]
    : null;
  return constantSuffix
    ? BOOKING_QUEUE_STEP_SHORT_NAMES[constantSuffix] || null
    : null;
};

// Returns the short key of the first incomplete prerequisite step for the
// given step, or null if it has no configured prerequisites or all of them
// are already complete. `bookingQueueDetails` is the raw
// Bookings\BookingQueueDetails collection: [{ queStepID, complete }, ...].
const getIncompleteBookingQueuePrerequisite = (
  shortName,
  bookingQueueDetails,
) => {
  const prereqs = BOOKING_QUEUE_STEP_PREREQUISITES[shortName];
  if (!prereqs?.length) return null;

  for (const prereqShort of prereqs) {
    const prereqEntry = bookingQueueDetails?.find(
      (d) => getBookingQueueStepShortName(d?.queStepID) === prereqShort,
    );
    if (prereqEntry?.complete !== true) {
      return prereqShort;
    }
  }
  return null;
};

// Reverse of getBookingQueueStepShortName: resolves a step's short key
// (e.g. "cp") back to its live BookingQueueStep defaultString
// (e.g. "11 CP (Remove from BQ)"), falling back to the short key itself if
// the reflist isn't loaded yet.
const getBookingQueueStepLabelByShortName = (shortName) => {
  const constantSuffix = Object.entries(BOOKING_QUEUE_STEP_SHORT_NAMES).find(
    ([, short]) => short === shortName,
  )?.[0];
  if (!constantSuffix) return shortName;

  const refList = refLists?.find(
    (rl) => rl?.refListName?.toLowerCase() === "bookingqueuestep",
  );
  const item = refList?.refListItems?.find(
    (rli) => rli?.constantName === `BOOKINGQUEUESTEP_${constantSuffix}`,
  );
  return item?.defaultString || shortName;
};

// Mirrors legacy's ValidateBookingQueueStepPrerequisites error text exactly
// (BookingsInterface.vbs): "Booking Queue Step 'X' is missing pre-requisite
// 'Y'." / "You must complete the 'Y' step or clear the 'X' step." Returned
// as two lines so callers can render the blank line between them.
const getMissingBookingQueuePrerequisiteMessageLines = (
  currentStepLabel,
  missingPrereqLabel,
) => [
  `Booking Queue Step '${currentStepLabel}' is missing pre-requisite '${missingPrereqLabel}'.`,
  `You must complete the '${missingPrereqLabel}' step or clear the '${currentStepLabel}' step.`,
];

const getRefListNameByID = (
  key,
  id,
  isStaff = false,
  longStringRequired = false,
) => {
  if (isNaN(id)) {
    return "";
  }
  let retStr = "";

  if (isStaff) {
    const staff = staffList?.find((sl) => sl?.staffId === id);
    if (isNonEmptyVariable(staff)) {
      if (longStringRequired) {
        return staff?.staffDescriptionCached || staff?.userName;
      } else {
        return staff?.userName;
      }
    } else {
      return "";
    }
  }

  const refList = refLists?.find(
    (rl) => rl?.refListName?.toLowerCase() === key?.toLowerCase(),
  );

  const refListItem = refList?.refListItems?.find(
    (rli) => rli?.refListItemGlobalId === id,
  );

  if (isNonEmptyVariable(refListItem)) {
    if (longStringRequired) {
      retStr = refListItem?.longString;
    } else {
      retStr = refListItem?.defaultString;
    }
  }

  return retStr;
};

// Like getRefListNameByID, but looks a ref list up by its numeric global ID
// (refListGlobalID - what a question's `selectRefListID` actually points at,
// same field getOptionsFromRefList matches on) instead of by name. Needed
// anywhere a Select-type question's own ref list has to be resolved without
// hardcoding a ref list name - e.g. showing the human-readable text for a
// QuestionSideEffect's answerToTriggerOnID or a QuestionAutoSet's
// answerRefListItemID, which can point into any ref list depending on which
// question they belong to (see GroupQuestionsDetails.jsx).
const getRefListItemTextByRefListID = (
  refListGlobalID,
  itemGlobalId,
  longStringRequired = false,
) => {
  if (
    !isNonEmptyVariable(refListGlobalID) ||
    !isNonEmptyVariable(itemGlobalId)
  ) {
    return "";
  }

  const refList = refLists?.find(
    (rl) => rl?.refListGlobalID == refListGlobalID,
  );
  const refListItem = refList?.refListItems?.find(
    (rli) => rli?.refListItemGlobalId === itemGlobalId,
  );

  if (!isNonEmptyVariable(refListItem)) return "";

  return longStringRequired && isNonEmptyVariable(refListItem?.longString)
    ? refListItem.longString
    : refListItem?.defaultString || "";
};

const getRefListStaffDesCacheByID = (id) => {
  if (isNaN(id)) return undefined;

  if (!Array.isArray(staffList) || staffList.length === 0) {
    return undefined; // critical
  }

  const staff = staffList.find((sl) => sl?.staffId === id);
  return staff?.staffDescriptionCached?.trim();
};

/**
 * Maps SQL data types to their corresponding HTML input types for form rendering.
 *
 * - Supports a variety of SQL data types, including character, numeric, date/time, and boolean types.
 * - Decides the most appropriate HTML input type based on SQL type characteristics.
 * - Provides a fallback to "text" for unsupported or unknown SQL types.
 *
 * @param {string} sqlType - The SQL data type to be mapped (e.g., "varchar(255)", "int", "datetime").
 * @returns {string} - The corresponding HTML input type (e.g., "text", "number", "date", "textarea").
 *
 * Example:
 * Input: sqlType = "varchar(255)"
 * Output: "text"
 *
 * Input: sqlType = "datetime"
 * Output: "datetime-local"
 *
 * Notes:
 * - Character types ("char", "varchar") are mapped to "text" or "textarea" based on their size.
 * - Integer and floating-point types are mapped to "number".
 * - Boolean types ("bit") are mapped to "select". This is a deliberate, if oddly-named, convention
 *   used throughout the static field configs (common_variable.js) to trigger a ref-list-backed
 *   dropdown (see DetentionsBioForm.jsx's "state"/"placeOfBirthCountry" fields) - it does NOT mean
 *   "checkbox". A literal type of "checkbox" (below) is what renders an actual checkbox; callers
 *   that build a `type` from raw SQL boolean-column metadata (e.g. convertApiFields for *Extra
 *   tables) must translate an unmapped "bit" column to "checkbox" themselves before calling this.
 * - Date/time types are mapped to respective HTML5 input types ("date", "datetime-local").
 */
const getSQLTypeToHTMLType = (sqlType) => {
  let type = ""; // Default to empty

  // Handle character types
  if (sqlType?.toLowerCase()?.includes("char")) {
    // Extract size within parentheses, e.g., varchar(255)
    const sizeMatch = sqlType?.match(/\((\d+)\)/); // Regular expression to extract size
    const size = sizeMatch ? parseInt(sizeMatch[1]) : null;

    // Decide between text or textarea
    type = size && size > 255 ? "textarea" : "text";
  }
  // Handle integer types
  else if (sqlType?.toLowerCase()?.includes("int")) {
    type = "number";
  }
  // Handle boolean types
  else if (sqlType?.toLowerCase()?.includes("bit")) {
    type = "select";
  }
  // Handle datetime types
  else if (sqlType?.toLowerCase()?.includes("datetime")) {
    type = "datetime"; // HTML5 datetime input
  }
  // Handle date types
  else if (sqlType?.toLowerCase()?.includes("date")) {
    type = "date"; // HTML5 date input
  }
  // Handle time-only types (SQL "time", or a caller-supplied "time"
  // override for a datetime column that should only show its time part —
  // see CivilPaperExtraFields.jsx's date/time-by-column-name split).
  else if (sqlType?.toLowerCase()?.includes("time")) {
    type = "time";
  }

  // Handle floating point or decimal numbers
  else if (
    sqlType?.toLowerCase()?.includes("float") ||
    sqlType?.toLowerCase()?.includes("decimal")
  ) {
    type = "number"; // Number type can handle decimals
  }
  // Handle large text fields
  else if (sqlType?.toLowerCase()?.includes("text")) {
    type = "textarea";
  }

  // Handle large text fields
  else if (sqlType?.toLowerCase()?.includes("checkbox")) {
    type = "checkbox";
  }

  // Fallback to text if no match
  else {
    type = "text";
  }

  return type;
};

const arrayToObject = async (fieldArray, clientValues) => {
  try {
    // Validate each field asynchronously and collect results
    const validationResults = await Promise.allSettled(
      fieldArray?.map(async (field) => {
        const validationRule = await checkFieldValidation(
          `${field?.tableName?.toLowerCase()}:${field?.columnName?.toLowerCase()}`,
          field?.type?.toLowerCase(),
          field?.label,
          clientValues,
        );
        return { [field?.columnName]: validationRule };
      }),
    );

    // Aggregate results into a single object
    const resultObject = validationResults.reduce((acc, result) => {
      if (result.status === "fulfilled") {
        // Merge the fulfilled result into the accumulated object
        return { ...acc, ...result.value };
      }

      // Optional: Log or handle rejected promises

      return acc;
    }, {});

    return resultObject; // Return the aggregated result object
  } catch (error) {
    throw new Error(error.message);
  }
};

/**
 * To diplay component if it has permission
 *
 * @param {string} paramContext - This arguments is used to check with condition if it is there or not
 * @returns {Boolean} - It will returns boolean value
 */
const checkRequiredField = (paramContext) => {
  const result = clientParamsLocal?.find(
    (cp) =>
      cp?.paramContext?.toLowerCase() === paramContext.toLowerCase() &&
      cp?.paramName.toLowerCase() === "required",
  )?.paramValue;

  return result;
};

/**
 * Calculates the age based on the given date of birth.
 *
 * @param {string|Date} dob - The date of birth. It can be a string or a Date object.
 * @returns {number} The calculated age.
 */
const findAgefromDOB = (dob) => {
  if (!isNonEmptyVariable(dob)) {
    return null; // Return null if dob is not provided
  }
  const dobDate = new Date(dob);
  const today = new Date();

  let age = today.getFullYear() - dobDate.getFullYear();
  const monthDiff = today.getMonth() - dobDate.getMonth();
  const dayDiff = today.getDate() - dobDate.getDate();

  // Adjust age if the birthday has not occurred yet this year
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age;
};

/**
 * Generates validation rules for a form field based on the provided parameter context.
 *
 * - Checks the `clientParams` array for matching parameter contexts.
 * - Adds validation rules for required fields and regular expressions.
 * - Supports custom error messages for both required and regex validations.
 *
 * @param {string} paramContext - The context parameter to check for validation rules.
 * @param {string} label - The label of the field, used in validation messages.
 * @returns {Array<Object>} - An array of validation rule objects.
 *
 * Example:
 * Input: paramContext = "username", label = "Username"
 * Output: [
 *   { required: true, message: "Username is required" },
 *   { pattern: /^regex$/, message: "Invalid format" }
 * ]
 */
const generateRules = (paramContext, label, setRequired = true) => {
  const location = useLocation()?.pathname;
  let validations = [...clientParamsLocal];
  if (location === "/bookings") {
    validations = [...bookingsPageValidations, ...clientParamsLocal];
  }

  const rules = [];
  validations?.forEach((cp) => {
    if (cp?.paramContext?.toLowerCase() === paramContext.toLowerCase()) {
      if (cp?.paramName == "required" && cp?.paramValue && setRequired) {
        rules.push({
          required: true,
          message: `${label} is required`,
        });
      } else if (cp?.paramName?.toLowerCase() === "regularexpression") {
        const [pattern, message] = cp?.paramValue?.split("||") || ["", ""];
        rules.push({
          pattern: new RegExp(`^${pattern}$`),
          message: message || "Invalid format",
        });
      }

      if (cp?.required) {
        rules.push({
          required: true,
          message: `${label} is required`,
        });
      }
    }
  });

  return rules;
};

// Legacy ValidateSSN (PciMercury Common/Validation.bas:277-313, Issue-Defect
// 522): any SSN value starting with "UNK" (case-insensitive) is accepted and
// stored as the literal "UNKNOWN". Everything else is returned untouched.
const normalizeUnknownSsn = (value) =>
  typeof value === "string" && /^unk/i.test(value) ? "UNKNOWN" : value;

const filteredUndefinedData = (data) => {
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined),
  );

  return filteredData;
};

const getRefListItemsbyName = (key) => {
  const refList = refLists?.find(
    (rl) => rl?.refListName?.toLowerCase() === key?.toLowerCase(),
  );

  // Extract and sort items, adding the default item at the start
  return refList?.refListItems?.filter((rli) => rli?.enabled) || [];
};

const getFullAddress = (address = {}) => {
  const line1 = [address?.addressStreetNumber, address?.addressStreet1]
    .filter(Boolean)
    .join(", ");

  const line2 = [address?.addressStreet2, address?.addressApartmentNumber]
    .filter(Boolean)
    .join(", ");

  const cityState = [address?.addressCity, address?.addressState]
    .filter(Boolean)
    .join(", ");

  return [line1, line2, cityState, address?.addressZip]
    .filter(Boolean)
    .join(", ");
};

const base64ToImage = (base64String) => {
  try {
    // Remove the data URL prefix if present
    const base64Data = base64String.replace(/^data:image\/\w+;base64,/, "");

    // Convert Base64 to a Blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "image/jpeg" });

    // Create an Object URL for the image
    return URL.createObjectURL(blob);
  } catch (error) {
    return null;
  }
};

const base64ToBlob = (base64String) => {
  // Remove the data URL prefix if present
  const base64Data = base64String.replace(/^data:image\/\w+;base64,/, "");

  // Decode Base64
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: "image/jpeg" });
};

const getFinalPath = (path) => {
  if (path?.includes("\\")) {
    const splitPath = path?.split("/");
    if (splitPath?.length === 4) {
      splitPath.shift();
      splitPath.shift();
    }

    return splitPath?.join("\\");
  }

  return path;
};

const checkGlobalClientParams = (
  paramName = "",
  paramContext = "",
  paramValue = "",
) => {
  if (!clientParamsLocal || clientParamsLocal.length === 0) return null;

  const filters = {};
  if (isNonEmptyVariable(paramName)) filters.paramName = paramName;
  if (isNonEmptyVariable(paramContext)) filters.paramContext = paramContext;
  if (isNonEmptyVariable(paramValue)) filters.paramValue = paramValue;

  const hasMatch = clientParamsLocal?.find((cp) =>
    Object.entries(filters).every(
      ([key, value]) => cp?.[key]?.toLowerCase() === value?.toLowerCase(),
    ),
  );

  return hasMatch || null;
};

// clientParams responses (and the Redux clientParamsList they feed) can contain
// multiple rows for the same paramName/paramContext - e.g. an old row left behind
// alongside a newer one saved from the app (see Config > Agency bar / ClientLogo).
// Always resolve to the most recently created row (highest clientParamID) instead
// of array order/index, which the backend doesn't guarantee to be meaningful.
const findLatestClientParam = (list, paramName, paramContext) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  return list.reduce((latest, p) => {
    const matches =
      p?.paramName?.toLowerCase() === paramName?.toLowerCase() &&
      p?.paramContext?.toLowerCase() === paramContext?.toLowerCase();
    if (!matches) return latest;
    return !latest || p?.clientParamID > latest?.clientParamID ? p : latest;
  }, null);
};

// Remove trailing "_U" from the API field name and convert it to camelCase.
function convertColumnName(original) {
  const stripped = original.replace(/_U+$/, "").replace("#", "");
  const parts = stripped.split("_");
  return parts
    .map((word, index) => {
      word = word.toLowerCase();
      return index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join("");
}

// Remove trailing "_U", replace underscores with spaces, and capitalize each word.
function convertLabel(original) {
  const stripped = original.replace(/_U+$/, "");
  const words = stripped.split("_");
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

// Convert API field definitions into our desired format for display.
const convertApiFields = (
  apiFields,
  tableName = "",
  skipColumnName = false,
) => {
  let tb = tableName;

  return apiFields.map((field) => {
    const original = field.columnName;
    let refListName = "";
    const convertedColumnName = skipColumnName
      ? original
      : convertColumnName(original);
    let type = field.characterMaximumLength
      ? `${field.dataType}(${field.characterMaximumLength})`
      : field.dataType;

    const clientParam = checkGlobalClientParams(
      "",
      `${tb + ":" + original}`,
      "",
    );
    if (isNonEmptyVariable(clientParam)) {
      if (clientParam?.paramName === "OverrideSelect") {
        type = "bit";
        refListName = clientParam?.paramValue;
      }
    }
    // A genuine boolean column with no "OverrideSelect" client param has no
    // ref list to draw options from (getSQLTypeToHTMLType's "bit" -> "select"
    // mapping is a convention for ref-list-backed dropdowns, not for real
    // booleans - see that function's notes). Render it as a checkbox instead,
    // matching the legacy ASP default (DataType = datatypeBoolean -> CreateCheckBox).
    else if (field.dataType?.toLowerCase() === "bit") {
      type = "checkbox";
    }
    const tableName = tb;
    const label = convertLabel(original);
    const required = field.isNullable === "NO";
    // Mark hidden fields: "BioExtraID" and "BioID" (case‑insensitive)
    const hidden = false;
    //   original.toLowerCase() === "bioextraid" ||
    //   original.toLowerCase() === "bioid";

    return {
      columnName: convertedColumnName,
      // Raw API/DB column name (e.g. "Alien_Registration_#_U"), kept
      // alongside the camelCased `columnName` used for form binding.
      // Global Client Params rows (ParamContext = "<Table>:<Column>") are
      // authored against this raw name — see DynamicForm's paramContext.
      originalColumnName: original,
      type,
      tableName,
      label,
      required,
      hidden,
      refListName,
    };
  });
};

const getLatestBookingsUtil = () => {
  return new Promise((resolve, reject) => {
    let url = API_ENDPOINTS.latestStaffBooking;
    if (isNonEmptyVariable(user) && user?.staffId === 1) {
      url = API_ENDPOINTS.latestBooking;
    }
    api
      .get(url)
      .then((response) => {
        if (response.status === 200 && response.data) {
          // dispatch(setBookings(response?.data));
          resolve(response?.data);
        }
      })
      .catch((error) => {
        console.error(
          "Request failed:",
          error.response ? error.response.data : error.message,
        );
      });
  });
};

const getBookingsByNumberUtil = (bookingNumber) => {
  return new Promise((resolve, reject) => {
    let url = API_ENDPOINTS.bookings + "/bookingNumber/id/" + bookingNumber;
    // if (isNonEmptyVariable(user) && user?.staffId === 1) {
    //   url = API_ENDPOINTS.latestBooking;
    // }
    api
      .get(url)
      .then((response) => {
        if (response.status === 200 && response.data) {
          // dispatch(setBookings(response?.data));
          resolve(response?.data);
        }
      })
      .catch((error) => {
        console.error(
          "Request failed:",
          error.response ? error.response.data : error.message,
        );
      });
  });
};

const imageToBase64 = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {}
};

const extractPath = (url) => {
  try {
    // Convert backslashes to forward slashes
    url = url.replace(/\\/g, "/");

    // Create a URL object
    const parsedUrl = new URL(url);

    // Get the pathname and remove the leading slash
    return parsedUrl.pathname.startsWith("/")
      ? parsedUrl.pathname.substring(1)
      : parsedUrl.pathname;
  } catch (error) {
    return null;
  }
};
const getVisibleFields = (personalInfo, detailLevel, person) => {
  if (person) {
    return personalInfo?.[person] || [];
  }

  if (detailLevel.toLowerCase() === "low") {
    return personalInfo?.Low || [];
  }

  return Object.values(personalInfo).flat();
};

const getDayjsObject = (date, time) => {
  if (!isNonEmptyVariable(date)) return null; // Handle missing date case

  return isNonEmptyVariable(time) ? dayjs(`${date} ${time}`) : dayjs(date);
};

const formatDropDownOptions = (data) => {
  const formattedData =
    Array.isArray(data) && data.length > 0
      ? data.map((value, key) => ({ key, value }))
      : [{ key: 0, value: " " }];

  return formattedData;
};

const formatDropDownOptionsWithRefListId = (data, ValueAsString = false) => {
  const formattedData =
    Array.isArray(data) && data.length > 0
      ? data.map(({ refListItemGlobalID, defaultString }) => ({
          label: defaultString,
          value: ValueAsString ? defaultString : refListItemGlobalID,
        }))
      : [{ key: 0, value: " " }];
  return formattedData;
};

/**
 *
 * @param {Number} num
 */
export function generateNextSequence(num) {
  return num + 1;
}

const asyncFilter = async (array, asyncPredicate) => {
  const results = await Promise.all(array.map(asyncPredicate));
  return array.filter((_, index) => results[index]);
};

const hasCheckSecurity = (
  type,
  entityType,
  permission,
  identifierParam,
  subModuleName = null,
  schemaName = null,
  fieldName = null,
  startingPoint = null,
) => {
  const jsonData = securityRules;
  let securityFunctionality = jsonData?.securityRulesFunctionality;
  let identifier = identifierParam;
  let subModuleID = "";
  //
  //

  if (
    entityType == Entity_types.module ||
    entityType === Entity_types.subModule
  ) {
    identifier = modulesList?.find(
      (m) => m?.moduleName?.toLowerCase() === identifierParam?.toLowerCase(),
    )?.moduleID;
    subModuleID = submoduleList?.find(
      (s) => s?.subModuleName?.toLowerCase() === subModuleName?.toLowerCase(),
    )?.subModuleID;
  }

  let result = false;

  switch (type) {
    // Criteria (GlobalCriteria row-level filtering) needs the actual record
    // being checked, which hasCheckSecurity's signature doesn't carry — use
    // hasCriteriaAccess(tableName, permission, record) for that instead.
    case Security_Types.criteria:
      result = true;
      break;

    // Correct Code
    case Security_Types.functionality:
      if (
        entityType == Entity_types.module ||
        entityType === Entity_types.subModule
      ) {
        const rules = jsonData?.securityRulesFunctionality || [];
        const matching = rules.filter(
          (item) =>
            item?.entityType?.toLowerCase() === entityType?.toLowerCase() &&
            (item.moduleId === identifier ||
              item.arbitrary === identifier ||
              item.reportId === identifier ||
              item.tableViewId === identifier) &&
            (isNonEmptyVariable(subModuleID)
              ? item.subModuleId === subModuleID
              : true) &&
            item?.ruleActive,
        );

        return resolveEffectiveAllow(matching, permission);
      } else if (entityType == "field" || entityType == "Field") {
        const rules = jsonData?.securityRulesFunctionality || [];

        // helper to normalize field names
        const extractFieldDetails = (name) => {
          if (Array.isArray(name)) {
            return {
              schema: schemaName,
              field: name[1] === 1 ? name[3] : (name[2] ?? ""),
            };
          }

          return {
            schema: schemaName, // fallback to existing
            field: name ?? "",
          };
        };

        const normalizeField = (name) =>
          (name ?? "") // handle null/undefined
            .toString() // convert to string safely
            .toLowerCase() // now safe
            .replace(/_/g, ""); // remove underscores

        const fieldDetails = extractFieldDetails(fieldName);
        const normalizedFieldName = normalizeField(fieldDetails.field);

        // No pre-check for "does any rule exist" needed: resolveEffectiveAllow
        // already defaults to allow when nothing matches at any tier, same as
        // PCI's System baseline (SystemAllowSetting starts as ALLRIGHTS).
        const matching = rules.filter(
          (item) =>
            item?.entityType?.toLowerCase() === "field" &&
            item.schemaName == schemaName &&
            item.ruleActive &&
            (normalizeField(item.fieldName) === normalizedFieldName ||
              item.fieldName === "*") &&
            (!item.startingPoint || item.startingPoint == startingPoint),
        );

        return resolveEffectiveAllow(matching, permission);
      } else if (entityType == Entity_types.arbitary) {
        const rules = jsonData?.securityRulesFunctionality || [];

        // Check if any rule exists for this entity + identifier
        const hasRule = rules.some(
          (item) =>
            item?.entityType?.toLowerCase() === entityType?.toLowerCase() &&
            item?.arbitrary === identifier,
        );

        // If no rule exists → allow (true)
        if (!hasRule) {
          if (startingPoint) {
            return false;
          } else {
            return true;
          }
        }

        // If rule(s) exist → resolve via the System/Global/Group/Personal
        // cascade (WhatAreMyRightsInternal).
        const matching = rules.filter(
          (item) =>
            item?.entityType?.toLowerCase() === entityType?.toLowerCase() &&
            item?.arbitrary === identifier &&
            item?.ruleActive,
        );

        return resolveEffectiveAllow(matching, permission);
      }
      break;

    case Security_Types.record:
      result = jsonData?.securityRulesRecords?.some(
        (item) =>
          item?.entityType?.toLowerCase() === entityType?.toLowerCase() &&
          item?.allowSetting?.[permission] &&
          !item?.denySetting?.[permission] &&
          item.startingPoint === identifier &&
          item.staffGroupsID === subModuleID,
      );
      break;
    default:
      throw new Error("Invalid type specified");
  }

  // return result && isAllowed(allowSetting, denySetting);
  return result;
};

const getModuleNameByID = (moduleID = "", subModuleID = "") => {
  const returnArr = [];
  if (
    isNonEmptyVariable(modulesList) &&
    modulesList?.length &&
    isEmptyObject(moduleID)
  ) {
    const moduleName = modulesList?.find(
      (m) => m?.moduleID === moduleID,
    )?.moduleName;
    returnArr.push(moduleName);
    if (
      isNonEmptyVariable(submoduleList) &&
      submoduleList?.length &&
      isEmptyObject(subModuleID)
    ) {
      const subModuleName = submoduleList?.find(
        (m) => m?.moduleID === moduleID && m?.subModuleID === subModuleID,
      )?.subModuleName;
      returnArr.push(subModuleName);
    }

    return returnArr;
  } else {
    return "";
  }
};

const getReportNameByID = (reportID = "") => {
  const returnArr = [];
  if (
    isNonEmptyVariable(reportList) &&
    reportList?.length &&
    isEmptyObject(reportID)
  ) {
    const reportName = reportList?.find(
      (r) => r?.reportID === reportID,
    )?.reportFileName;
    returnArr.push(reportName);

    return returnArr;
  } else {
    return "";
  }
};

const getTableViewsNameByID = (tableViewId = "") => {
  const returnArr = [];
  if (
    isNonEmptyVariable(tableViewsList) &&
    tableViewsList?.length &&
    isEmptyObject(tableViewId)
  ) {
    const tableViewsName = tableViewsList?.find(
      (t) => t?.tableViewID === tableViewId,
    )?.tableViewName;
    returnArr.push(tableViewsName);

    return returnArr;
  } else {
    return "";
  }
};

/**
 * Reset all fields except the ones you want to preserve
 *
 * @param {Object} form - AntD form instance
 * @param {string[]} preserveFields - array of field names you DON'T want to reset
 */
const resetExcept = (form, preserveFields = []) => {
  const allFields = form.getFieldsValue();

  // Filter out fields that should NOT be preserved
  const fieldsToReset = Object.keys(allFields).filter(
    (name) => !preserveFields.includes(name),
  );

  form.resetFields(fieldsToReset);
};

const generateCurrentLocation = (inmateTrack, agency = "") => {
  if (!isNonEmptyVariable(inmateTrack)) return "";

  const builders = {
    "In Jail": () => {
      if (inmateTrack?.inJailType === "Cell") {
        const block = isNonEmptyVariable(inmateTrack?.block)
          ? inmateTrack.block
          : "";
        const cell = isNonEmptyVariable(inmateTrack?.cell)
          ? inmateTrack.cell
          : "";
        const bed = isNonEmptyVariable(inmateTrack?.bed) ? inmateTrack.bed : "";
        let Agency = isNonEmptyVariable(agency) ? `{${agency}}` : "";
        return `Cell ${block}${cell ? `:${cell}` : ""}${
          bed ? `:${bed}` : ""
        } ${Agency}`;
      }
      const holding = isNonEmptyVariable(inmateTrack?.holdingCell)
        ? inmateTrack.holdingCell
        : "";
      return `Holding (${holding})`;
    },

    Loan: () => {
      const destination = isNonEmptyVariable(inmateTrack?.loanDestination)
        ? inmateTrack.loanDestination
        : "";
      const type = isNonEmptyVariable(inmateTrack?.loanType)
        ? ` (${inmateTrack.loanType})`
        : "";
      return `Loan to ${destination}${type}`.trim();
    },

    "Perm Housing Change": () => {
      const dest = isNonEmptyVariable(inmateTrack?.transportDestination)
        ? inmateTrack.transportDestination
        : "";

      return `Perm Housing Change to ${dest}`.trim();
    },

    Transport: () => {
      const dest = isNonEmptyVariable(inmateTrack?.transportDestination)
        ? inmateTrack.transportDestination
        : "";
      return `Transport to ${dest}`.trim();
    },
  };

  return builders[inmateTrack.inmateLocation]?.() || "";
};

function isValidPassword(password) {
  // Regex explanation:
  // ^(?=.*[a-z])       -> at least one lowercase
  // (?=.*[A-Z])        -> at least one uppercase
  // (?=.*\d)           -> at least one number
  // (?=.*[^A-Za-z0-9]) -> at least one special character
  // .{12,}             -> at least 12 characters long
  // $
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;

  return regex.test(password);
}

// This app isn't consistent about which of ParamName/ParamContext holds the "bucket"
// (e.g. "Globals") vs. the specific key - the backend's own ClientLogo lookup stores it
// as paramName="Globals"/paramContext="ClientLogo" (reversed from the classic ASP
// convention of ParamContext="Globals"/ParamName=<key>). Match either ordering so this
// doesn't depend on which convention a given row was entered with.
function findClientParamValue(clientParamsList, paramName, paramContext) {
  if (!Array.isArray(clientParamsList)) return null;
  const name = paramName.toLowerCase();
  const context = paramContext.toLowerCase();
  const match = clientParamsList.find((p) => {
    const pName = p?.paramName?.toLowerCase();
    const pContext = p?.paramContext?.toLowerCase();
    return (
      (pName === name && pContext === context) ||
      (pName === context && pContext === name)
    );
  });
  return match?.paramValue || null;
}

// Validates against the ClientParams row (ParamName='RegularExpression', ParamContext='Staff:Password')
// that the classic ASP app enforces in SetPasswordForCurrentUser (RecordStructures.asp). Falls back to
// the hardcoded rule below only when that param isn't configured for this client.
function validateStaffPassword(password, clientParamsList) {
  if (!password) return { valid: true, message: "" };

  const rawValue = findClientParamValue(
    clientParamsList,
    "RegularExpression",
    "Staff:Password",
  );

  if (rawValue) {
    const [pattern, help] = rawValue.split("||");
    if (pattern) {
      try {
        const isMatch = new RegExp(pattern).test(password);
        return isMatch
          ? { valid: true, message: "" }
          : {
              valid: false,
              message: help || "Password does not meet the required criteria.",
            };
      } catch {
        // configured pattern is invalid; fall through to the default rule
      }
    }
  }

  return isValidPassword(password)
    ? { valid: true, message: "" }
    : {
        valid: false,
        message:
          "Password must be at least 12 characters long and include uppercase, lowercase, number, and special character.",
      };
}

// Mirrors the "Globals"/"PasswordChangeEveryXDays" setting (logonGeneric.asp ValidateLogon).
// 0 (or unset) means expiration checking is disabled, matching the ASP default.
function getPasswordExpiryDays(clientParamsList) {
  const rawValue = findClientParamValue(
    clientParamsList,
    "PasswordChangeEveryXDays",
    "Globals",
  );
  const days = parseInt(rawValue, 10);
  return Number.isFinite(days) && days > 0 ? days : 0;
}

function parseServerDateTime(value) {
  if (!value) return null;
  const match =
    /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/.exec(
      String(value).trim(),
    );
  if (match) {
    const [, mm, dd, yyyy, hh = "0", min = "0", ss = "0"] = match;
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(min),
      Number(ss),
    );
  }
  const fallback = new Date(value);
  return isNaN(fallback.getTime()) ? null : fallback;
}

// Expires exactly `expiryDays` * 24h after PasswordLastUpdated - e.g. last updated
// 07/17/2026 16:17:00 with expiryDays=1 expires at 07/18/2026 16:17:00, not at midnight.
function isStaffPasswordExpired(passwordLastUpdated, expiryDays) {
  if (!expiryDays || expiryDays <= 0) return false;
  const last = parseServerDateTime(passwordLastUpdated);
  if (!last) return false;
  const expiresAt = last.getTime() + expiryDays * 24 * 60 * 60 * 1000;
  return Date.now() >= expiresAt;
}

function removeNullFromText(str) {
  return str
    .replace(/\b(null|undefined)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanObject(obj) {
  if (Array.isArray(obj)) {
    // If it's an array, recursively clean each item and filter out empty values
    return obj
      .map(cleanObject) // Recursively clean each element
      .filter(
        (value) =>
          value !== undefined &&
          value !== null &&
          value !== "" &&
          (Array.isArray(value) ? value.length > 0 : true) &&
          (typeof value === "object" ? Object.keys(value).length > 0 : true),
      );
  } else if (typeof obj === "object" && obj !== null) {
    // If it's an object, recursively clean its properties
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const cleanedValue = cleanObject(value); // Recursively clean the value
      if (
        cleanedValue !== undefined &&
        cleanedValue !== null &&
        cleanedValue !== "" &&
        (typeof cleanedValue === "object"
          ? Object.keys(cleanedValue).length > 0
          : true)
      ) {
        acc[key] = cleanedValue; // Add only non-empty values
      }
      return acc;
    }, {});
  }
  // If it's a primitive value, return it as-is
  return obj;
}
const deepReplaceEmptyWithNull = (value) => {
  if (value === "") return null;

  if (Array.isArray(value)) {
    return value.map((v) => deepReplaceEmptyWithNull(v));
  }

  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [
        key,
        deepReplaceEmptyWithNull(val),
      ]),
    );
  }

  return value;
};

function buildLineUpWhereClauseStructure(filters) {
  const map = Object.fromEntries(filters.map((f) => [f.filterKey, f.value]));

  // Height range
  const heightMin = map?.height - (map?.heightPlusOrMinus || 0);
  const heightMax = map?.height + (map?.heightPlusOrMinus || 0);

  // Weight range
  const weightMin = map?.weight - (map?.weightPlusOrMinus || 0);
  const weightMax = map?.weight + (map?.weightPlusOrMinus || 0);

  // Age range
  const ageMin = map?.ageAtEntry - (map?.agePlusOrMinus || 0);
  const ageMax = map?.ageAtEntry + (map?.agePlusOrMinus || 0);

  // Build the structure
  const structure = `((([JACKETS\\BIO|BIOID\\BIOPHOTOS|BIOID\\PHOTOS|PHOTOID.]IMAGE_TYPE LIKE '${
    map?.imageType
  }%') 
  AND (([JACKETS\\BIO|BIOID\\BIOPHOTOS|BIOID.]LINEUPFLAG IS NOT NULL 
  AND [JACKETS\\BIO|BIOID\\BIOPHOTOS|BIOID.]LINEUPFLAG = 1))) 
  AND (([JACKETS\\BIO|BIOID.]RACE LIKE '${map?.race}%') 
  AND (([JACKETS\\BIO|BIOID.]SEX LIKE '${map?.gender}%') 
  AND ([JACKETS\\BIO|BIOID.]HAIR LIKE '${map?.hair}%') 
  AND ([JACKETS\\BIO|BIOID.]EYES LIKE '${map?.eyes}%') 
  AND ([JACKETS\\BIO|BIOID.]GLASSES LIKE '${map?.glasses?.trim()}%') 
  AND ([JACKETS\\BIO|BIOID.]HAIRLENGTH LIKE '${map?.hairLength}%') 
  AND ([JACKETS\\BIO|BIOID.]FACIALHAIR LIKE '${map?.facialHair}%') 
  AND ([JACKETS\\BIO|BIOID.]COMPLEXION LIKE '${map?.complexion?.trim()}%') 
  AND ([JACKETS\\BIO|BIOID.]ETHNICITY LIKE '${map?.ethnicity}%'))) 
  AND (([JACKETS\\BIO|BIOID.]HEIGHT >= ${heightMin}) 
  AND ([JACKETS\\BIO|BIOID.]HEIGHT <= ${heightMax}) 
  AND ([JACKETS\\BIO|BIOID.]WEIGHT >= ${weightMin}) 
  AND ([JACKETS\\BIO|BIOID.]WEIGHT <= ${weightMax}) 
  AND ([JACKETS\\BIO|BIOID.]AGEATENTRY >= ${ageMin}) 
  AND ([JACKETS\\BIO|BIOID.]AGEATENTRY <= ${ageMax}))`;

  return structure;
}

const getFormattedDayjsObjectToString = (
  dateTime,
  format = "MM/DD/YYYY HH:mm",
) => {
  if (!isNonEmptyVariable(dateTime)) return null;
  const parsed = dayjs(dateTime);
  return parsed.isValid() ? parsed.format(format) : null;
};

const normalizeWarrantResponse = (data, imd = null) => {
  const root = data?.warrantRoot ?? {};
  const warrant = root?.warrants?.[0] ?? {};

  const jacket = root?.jackets?.[0] ?? {};
  const bio = jacket?.bio?.[0] ?? {};

  return {
    warrantRootID: root.warrantRootID,
    jacketID: root.jacketID,
    dateAdded: root.dateAdded,
    timeAdded: root.timeAdded,
    creatorStaffID: root.creatorStaffID,
    addressToSendToStateID: root.addressToSendToStateID ?? null,

    warrantId: warrant.warrantId,
    warrantNumber: warrant.warrantNumber,
    warrantType: warrant.warrantType,
    charge: warrant.charge,
    chargeCount: warrant.chargeCount,
    dateIssued: warrant.dateIssued,
    dateReceived: warrant.dateReceived,
    dateProcessed: warrant.dateProcessed,
    ori: warrant.ori,
    agency: warrant.agency,
    docket: warrant.docket,
    otn: warrant.otn,
    jurisdiction: warrant.jurisdiction,
    inGcic: warrant.inGcic,
    comments: warrant.comments,
    offenseCode: warrant.offenseCode,
    offenseDate: warrant.offenseDate,
    offenseTime: warrant.offenseTime,
    affidavit: warrant.affidavit,
    bondType: warrant.bondType,
    bondDate: warrant.bondDate,
    bondedBy: warrant.bondedBy,
    bondAmount: warrant.bondAmount,
    bondStreet1: warrant.bondStreet1,
    bondStreet2: warrant.bondStreet2,
    bondCity: warrant.bondCity,
    bondState: warrant.bondState,
    bondZip: warrant.bondZip,
    bondPhone: warrant.bondPhone,
    disposition: warrant.disposition,
    outstandingFlag: warrant.outstandingFlag,
    judgeName: warrant.judgeName,
    prosecutor: warrant.prosecutor,
    prosecutorStreet1: warrant.prosecutorStreet1,
    prosecutorStreet2: warrant.prosecutorStreet2,
    prosecutorCity: warrant.prosecutorCity,
    prosecutorState: warrant.prosecutorState,
    prosecutorZip: warrant.prosecutorZip,
    prosecutorPhone: warrant.prosecutorPhone,
    prosecutingAgency: warrant.prosecutingAgency,
    prosecutorAddressId: warrant.prosecutorAddressId,
    assignedTo: warrant.assignedTo,
    pending: warrant.pending,
    sealed: warrant.sealed,
    useAlias: warrant.useAlias,
    bioAliasId: warrant.bioAliasId,
    notifyVictim: warrant.notifyVictim,
    victimHasBeenNotified: warrant.victimHasBeenNotified,
    ordinance: warrant.ordinance,

    offenceCity: warrant.offenceCity ?? null,
    statusId: warrant.statusId ?? null,
    crnNo: warrant.crnNo ?? null,
    juvenileWarrant: warrant.juvenileWarrant ?? null,
    courtNameId: warrant.courtNameId ?? null,
    arrestingOfficerStaffId: warrant.arrestingOfficerStaffId ?? null,

    warrantCharges: warrant.warrantCharges ?? [],
    warrantAffiant: warrant.warrantAffiant ?? [],
    warrantCashAccounts:
      warrant.warrantCashAccounts ?? warrant.warrantCashAccount ?? [],
    warrantExtraditionInfo: warrant.warrantExtraditionInfo ?? [],
    warrantStateInfo: warrant.warrantStateInfo ?? [],
    warrantVictim: warrant.warrantVictim ?? [],
    warrantsStateSubmissions: warrant.warrantsStateSubmissions ?? [],
    warrantLinkedDocuments: warrant.warrantLinkedDocument ?? [],
    serviceDocuments: warrant.serviceDocuments ?? [],
    warrantsExtra: warrant.warrantsExtra ?? [],

    bookingLinkedDocuments: warrant.warrantLinkedDocument ?? [],

    jacketId: jacket.jacketId,
    jacket: { ...jacket },

    bio: [
      {
        ...bio,
        dob: bio?.dob ?? null,
      },
    ],

    warrants: (root?.warrants ?? []).map((w) => {
      const { jackets, ...warrantWithoutJackets } = w;
      const warrantId = w.warrantId ?? w.warrantID ?? null;
      return {
        ...warrantWithoutJackets,
        warrantCharges: w.warrantCharges ?? [],
        warrantAffiant: w.warrantAffiant ?? [],
        warrantCashAccounts:
          w.warrantCashAccounts ?? w.warrantCashAccount ?? [],
        warrantExtraditionInfo: w.warrantExtraditionInfo ?? [],
        warrantStateInfo: w.warrantStateInfo ?? [],
        warrantVictim: w.warrantVictim ?? [],
        warrantsStateSubmissions: w.warrantsStateSubmissions ?? [],
        warrantLinkedDocuments: w.warrantLinkedDocument ?? [],
        serviceDocuments: w.serviceDocuments ?? [],
        warrantsExtra: w.warrantsExtra ?? [],
        // cdInvolvements come back flattened on the WarrantRoot — pull out
        // the entries scoped to this specific warrant via relatedObjectID.
        // String() guards against a number/string type mismatch between
        // the backend-assigned warrantId and relatedObjectID.
        cdInvolvements: (root?.cdInvolvements ?? []).filter(
          (inv) =>
            inv.relatedObjectType === "Warrants" &&
            warrantId != null &&
            String(inv.relatedObjectID) === String(warrantId),
        ),
      };
    }),

    // Without imd, use the WarrantRoot's own cdInvolvements - the backend
    // returns every flag on the jacket there, complete (dangerTypeId/
    // gangTypeId/medicalTypeId...). Starting from [] left only the
    // flags/jacket copies, which carry no sub-type: the Flags dialog lost
    // its "[Type]. " prefix and the next Warrants save nulled the saved
    // sub-type (confirmed 2026-09-30). Every row is still sent on save -
    // omitting rows deletes those flags on the backend.
    cdInvolvements: isNonEmptyVariable(imd)
      ? imd?.cdInvolvements
      : (root?.cdInvolvements ?? []),
  };
};

// Legacy ASP (CashAccountingInterfaceCommon.asp) binds Cash/Trust Account as
// a single record per civil paper — no "list of accounts" ever existed in
// that UI. The real civilPapersNewCashAccount API response is still an
// array though, and a past save-flow bug (since fixed — the un-indexed
// trustAccountingNew read and the stray form.resetFields() object
// corruption, both in CivilPaperDetails.jsx) left some already-saved civil
// papers with several duplicate container rows instead of one. Always
// picking array[0] blindly can surface one of those stray duplicates
// instead of the real account, so this picks the canonical one deterministically:
// the entry with the lowest civilPapersNewCashAccountId (the first one ever
// created for this civil paper), falling back to the first entry when none
// have a real id yet (a brand-new, unsaved account).
const getCanonicalCashAccount = (civilPapersNewCashAccount) => {
  const list = civilPapersNewCashAccount ?? [];
  if (list.length <= 1) return list[0] ?? null;
  const withId = list.filter((a) =>
    isNonEmptyVariable(a?.civilPapersNewCashAccountId),
  );
  if (withId.length === 0) return list[0];
  return withId.reduce((lowest, a) =>
    a.civilPapersNewCashAccountId < lowest.civilPapersNewCashAccountId
      ? a
      : lowest,
  );
};

export {
  SetReflist,
  SetClientParam,
  SetSecurityRules,
  SetModulesList,
  isLoggedInStaff,
  isLoggedInStaffInGroup,
  refreshLoggedInUserSecurity,
  isEmptyObject,
  objectToArray,
  toQueryString,
  isNonEmptyVariable,
  debounce,
  generateArray,
  checkField,
  checkFieldValidation,
  getSQLTypeToHTMLType,
  getOptionsFromRefList,
  getRefListItemGlobalIdByDefaultString,
  getRefListItemTextByRefListID,
  isRemoveFromQueueStep,
  getBookingQueueStepConstantName,
  getBookingQueueStepShortName,
  getIncompleteBookingQueuePrerequisite,
  getBookingQueueStepLabelByShortName,
  getMissingBookingQueuePrerequisiteMessageLines,
  getRefListNameByID,
  getRefListStaffDesCacheByID,
  arrayToObject,
  checkRequiredField,
  findAgefromDOB,
  generateRules,
  normalizeUnknownSsn,
  filteredUndefinedData,
  getRefListItemsbyName,
  getFullAddress,
  base64ToImage,
  base64ToBlob,
  getFinalPath,
  checkGlobalClientParams,
  findLatestClientParam,
  convertApiFields,
  convertColumnName,
  getLatestBookingsUtil,
  getBookingsByNumberUtil,
  imageToBase64,
  extractPath,
  getVisibleFields,
  getDayjsObject,
  formatDropDownOptions,
  formatDropDownOptionsWithRefListId,
  asyncFilter,
  hasCheckSecurity,
  getModuleNameByID,
  getReportNameByID,
  getTableViewsNameByID,
  resetExcept,
  generateCurrentLocation,
  isValidPassword,
  validateStaffPassword,
  getPasswordExpiryDays,
  findClientParamValue,
  isStaffPasswordExpired,
  removeNullFromText,
  cleanObject,
  deepReplaceEmptyWithNull,
  buildLineUpWhereClauseStructure,
  getFormattedDayjsObjectToString,
  normalizeWarrantResponse,
  getCanonicalCashAccount,
};
